import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { format, parseISO, differenceInDays } from "date-fns";
import { toast } from "sonner";
import { FadeIn } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import AvailabilityPeek from "@/components/AvailabilityPeek";
import GoogleReviewsCarousel from "@/components/GoogleReviewsCarousel";
import { ROOM_TYPES, MEAL_PLANS, DIRECT_BOOKING_PERKS } from "@/lib/site";
import { CalendarIcon, Minus, Plus, ShieldCheck, Utensils, Coffee, Gamepad2, Car } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Booking() {
  const loc = useLocation();
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    guests: 2,
    message: "",
  });
  const initial = Object.fromEntries(ROOM_TYPES.map((r) => [r.id, 0]));
  if (loc.state?.room && initial[loc.state.room] !== undefined) initial[loc.state.room] = 1;
  else initial[ROOM_TYPES[0].id] = 1;
  const [roomsSelected, setRoomsSelected] = useState(initial);
  const [mealPlan, setMealPlan] = useState("none"); // "none" | "AP" | "MAP"
  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [loading, setLoading] = useState(false);
  const [blockedDates, setBlockedDates] = useState([]);
  const [payConfig, setPayConfig] = useState({ enabled: false, advance_percent: 100, gst_percent: 5 });
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    axios.get(`${API}/bookings/availability`)
      .then(({ data }) => setBlockedDates((data.blocked_dates || []).map((d) => parseISO(d))))
      .catch(() => setBlockedDates([]));
    axios.get(`${API}/payments/config`).then(({ data }) => setPayConfig(data)).catch(() => {});
  }, []);

  // When the confirmation / payment view mounts, snap the viewport to the very top so
  // the guest lands on the confirmed booking summary instead of the old form's scroll position.
  useEffect(() => {
    if (!booking) return;
    const lenis = window.__lenis__;
    if (lenis && typeof lenis.scrollTo === "function") {
      lenis.scrollTo(0, { immediate: true, force: true });
    }
    window.scrollTo(0, 0);
  }, [booking]);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const bump = (id, delta) => setRoomsSelected((prev) => {
    const room = ROOM_TYPES.find((r) => r.id === id);
    const next = Math.max(0, Math.min(room.inventory, (prev[id] || 0) + delta));
    return { ...prev, [id]: next };
  });

  const nights = range.from && range.to ? Math.max(differenceInDays(range.to, range.from), 1) : 0;
  const totalRooms = Object.values(roomsSelected).reduce((a, b) => a + b, 0);
  const capacity = ROOM_TYPES.reduce((sum, r) => sum + (roomsSelected[r.id] || 0) * r.sleeps, 0);
  const roomsSubtotal = ROOM_TYPES.reduce((sum, r) => sum + (roomsSelected[r.id] || 0) * r.price_inr * nights, 0);

  // Meal plan — per couple per night; couples = ceil(guests/2)
  const guestsNum = Number(form.guests) || 0;
  const couples = Math.max(1, Math.ceil(guestsNum / 2));
  const mealPlanObj = MEAL_PLANS.find((m) => m.id === mealPlan);
  const mealPlanCost = mealPlanObj && nights > 0 ? mealPlanObj.rate_per_couple_per_night * couples * nights : 0;

  const subtotal = roomsSubtotal + mealPlanCost;
  const gst = Math.round((subtotal * (payConfig.gst_percent || 0)) / 100);
  const grandTotal = subtotal + gst;
  const advance = Math.max(Math.round((grandTotal * payConfig.advance_percent) / 100), 0);
  const guests = guestsNum;
  const capacityShort = guests > 0 && capacity < guests;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone) return toast.error("Please fill in name, email and phone.");
    if (!range.from || !range.to) return toast.error("Please pick check-in and check-out dates.");
    if (totalRooms === 0) return toast.error("Select at least one room.");
    if (capacityShort) return toast.error(`Selected rooms only sleep ${capacity} — please add more rooms.`);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/bookings`, {
        ...form,
        guests: Number(form.guests),
        check_in: format(range.from, "yyyy-MM-dd"),
        check_out: format(range.to, "yyyy-MM-dd"),
        rooms_selected: Object.fromEntries(Object.entries(roomsSelected).filter(([, v]) => v > 0)),
        meal_plan: mealPlan,
      });
      setBooking(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const startPayment = useCallback(async () => {
    if (!booking) return;
    if (!(await loadRazorpay())) return toast.error("Could not load payment gateway.");
    try {
      const { data } = await axios.post(`${API}/payments/create-order`, { booking_id: booking.id });
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount_paise,
        currency: "INR",
        name: "Tikratoli Farm Resorts",
        description: `${booking.room_type || "Reservation"} · ${nights} night${nights > 1 ? "s" : ""}`,
        order_id: data.order_id,
        prefill: data.guest,
        theme: { color: "#D97645" },
        handler: async (resp) => {
          try {
            await axios.post(`${API}/payments/verify`, {
              booking_id: booking.id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment received — your booking is confirmed.");
            setBooking({ ...booking, payment_status: "paid", status: "confirmed" });
          } catch { toast.error("Payment could not be verified. Contact us."); }
        },
        modal: { ondismiss: () => toast("Payment cancelled — you can retry any time.") },
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start payment.");
    }
  }, [booking, nights]);

  if (booking) {
    const isPaid = booking.payment_status === "paid";
    return (
      <main data-testid="booking-page" className="pt-32 pb-24">
        <section className="mx-auto max-w-[900px] px-6 lg:px-12">
          <FadeIn>
            <div className="stamp mb-6">Vol · VIII · Reserve</div>
            <h1 className="font-display text-5xl md:text-7xl leading-[0.95] text-[#F2E7D0]">
              {isPaid ? <>Confirmed — <em className="italic text-[#D97645]">the fire is lit</em>.</> : <>Reservation received.</>}
            </h1>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6 border-t border-white/10 pt-8">
              <div><div className="chapter-num">Rooms</div><div className="mt-2 font-display text-2xl text-[#F2E7D0]">{booking.room_type}</div></div>
              <div><div className="chapter-num">Nights</div><div className="mt-2 font-display text-3xl text-[#F2E7D0]">{nights}</div></div>
              <div><div className="chapter-num">Dates</div><div className="mt-2 text-[#F2E7D0]">{booking.check_in} → {booking.check_out}</div></div>
              {booking.meal_plan && booking.meal_plan !== "none" && (
                <div data-testid="confirmed-meal-plan">
                  <div className="chapter-num">Meal plan</div>
                  <div className="mt-2 text-[#F2E7D0]">{booking.meal_plan} · ₹ {(booking.meal_plan_cost_inr || 0).toLocaleString("en-IN")}</div>
                </div>
              )}
              <div><div className="chapter-num">Total (incl. GST)</div><div className="mt-2 text-[#F2E7D0]">₹ {grandTotal.toLocaleString("en-IN")}</div></div>
            </div>
            <div className="mt-10 border border-white/10 p-6">
              {isPaid ? (
                <div className="flex items-start gap-3">
                  <ShieldCheck size={20} className="text-[#D97645] mt-1" />
                  <div>
                    <div className="font-display text-2xl text-[#F2E7D0]">Payment of ₹ {booking.amount_paid_inr?.toLocaleString("en-IN") || advance.toLocaleString("en-IN")} received.</div>
                    <p className="mt-2 text-[#B8A891] text-sm">This includes {payConfig.gst_percent}% GST. A confirmation email is on its way.</p>
                  </div>
                </div>
              ) : payConfig.enabled ? (
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <div className="chapter-num">Confirm now</div>
                    <div className="mt-1 font-display text-2xl text-[#F2E7D0]">Pay ₹ {advance.toLocaleString("en-IN")}</div>
                    <p className="mt-1 text-sm text-[#B8A891]">Cards, UPI, netbanking supported.</p>
                  </div>
                  <Button data-testid="pay-advance-btn" onClick={startPayment} className="rounded-none bg-[#D97645] hover:bg-[#C0602F] text-[#F2E7D0] font-mono-label h-12 px-8">Proceed to pay</Button>
                </div>
              ) : (
                <p className="text-[#B8A891] text-sm">Someone from the estate will write back within one working day.</p>
              )}
            </div>

            {/* Cancellation policy */}
            {booking.status !== "cancelled" && (
              <div className="mt-6 border border-white/10 p-6" data-testid="cancellation-policy">
                <div className="chapter-num mb-2">Cancellation Policy</div>
                <p className="text-[#B8A891] text-sm leading-relaxed">
                  Free cancellation any time <span className="text-[#F2E7D0]">before your check-in date</span> — full refund credited to the original payment source in 5–7 working days.
                  Once check-in day arrives, cancellations are <span className="text-[#F2E7D0]">non-refundable</span>.
                </p>
                <Button
                  data-testid="cancel-booking-btn"
                  onClick={async () => {
                    if (!window.confirm("Cancel this booking? You will receive a full refund if paid.")) return;
                    try {
                      const { data } = await axios.post(`${API}/bookings/${booking.id}/cancel`);
                      toast.success(data.refund_inr ? `Booking cancelled. ₹${data.refund_inr.toLocaleString("en-IN")} refunded.` : "Booking cancelled.");
                      setBooking({ ...booking, status: "cancelled", payment_status: data.refund_inr ? "refunded" : booking.payment_status });
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Could not cancel.");
                    }
                  }}
                  variant="outline"
                  className="mt-4 rounded-none bg-transparent border border-white/30 hover:border-red-400 hover:text-red-400 text-[#F2E7D0] font-mono-label h-10 px-6"
                >
                  Cancel booking
                </Button>
              </div>
            )}

            {booking.status === "cancelled" && (
              <div className="mt-6 border border-white/10 p-6 text-[#B8A891]" data-testid="cancelled-notice">
                This booking has been cancelled.{booking.payment_status === "refunded" ? " Refund has been initiated to your original payment source." : ""}
              </div>
            )}
          </FadeIn>
        </section>
      </main>
    );
  }

  return (
    <main data-testid="booking-page" className="pt-32 pb-24">
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12">
        <FadeIn>
          <div className="stamp mb-8">Vol · VIII · Reserve</div>
          <h1 className="font-display text-6xl md:text-8xl leading-[0.95] text-[#F2E7D0] max-w-5xl">
            Reserve your <em className="italic text-[#D97645]">stay</em>.
          </h1>
          <p className="mt-8 max-w-xl text-[#B8A891] text-lg">
            {payConfig.enabled
              ? `Pick your rooms and dates. Pay the full amount (incl. ${payConfig.gst_percent}% GST) by card or UPI — your reservation is confirmed instantly. Free cancellation any time before check-in.`
              : "Submit a reservation request. We will confirm and revert."}
          </p>
        </FadeIn>

        {/* Social proof strip — live Google reviews */}
        <FadeIn delay={0.15} className="mt-10 max-w-2xl">
          <GoogleReviewsCarousel variant="strip" />
        </FadeIn>
      </section>

      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 py-16">
        <FadeIn>
          <form data-testid="booking-form" onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div><Label className="font-mono-label text-[#B8A891]">Full name</Label><Input data-testid="booking-name" value={form.full_name} onChange={change("full_name")} className="mt-2 bg-transparent border-white/20 rounded-none h-12" /></div>
            <div><Label className="font-mono-label text-[#B8A891]">Email</Label><Input data-testid="booking-email" type="email" value={form.email} onChange={change("email")} className="mt-2 bg-transparent border-white/20 rounded-none h-12" /></div>
            <div><Label className="font-mono-label text-[#B8A891]">Phone</Label><Input data-testid="booking-phone" value={form.phone} onChange={change("phone")} className="mt-2 bg-transparent border-white/20 rounded-none h-12" /></div>
            <div>
              <Label className="font-mono-label text-[#B8A891]">Total guests</Label>
              <Input data-testid="booking-guests" type="number" min={1} max={64} value={form.guests} onChange={change("guests")} className="mt-2 bg-transparent border-white/20 rounded-none h-12" />
              {capacityShort && (
                <p className="mt-1 text-xs text-red-400">Selected rooms only sleep {capacity} — add more rooms below.</p>
              )}
            </div>

            <div className="md:col-span-2">
              <Label className="font-mono-label text-[#B8A891] mb-3 block">Pick your rooms · max 4 per category</Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {ROOM_TYPES.map((r) => (
                  <div key={r.id} data-testid={`room-picker-${r.id}`} className="border border-white/10 p-5">
                    <div className="font-display text-2xl text-[#F2E7D0]">{r.name}</div>
                    <div className="mt-1 text-xs font-mono-label text-[#B8A891]">Sleeps {r.sleeps} · ₹ {r.price_inr.toLocaleString("en-IN")}/night</div>
                    <div className="mt-5 flex items-center justify-between">
                      <button type="button" data-testid={`decrement-${r.id}`} onClick={() => bump(r.id, -1)} className="w-10 h-10 border border-white/20 flex items-center justify-center hover:border-[#D97645] text-[#F2E7D0]"><Minus size={14} /></button>
                      <div className="font-display text-3xl text-[#F2E7D0] w-12 text-center" data-testid={`count-${r.id}`}>{roomsSelected[r.id] || 0}</div>
                      <button type="button" data-testid={`increment-${r.id}`} onClick={() => bump(r.id, 1)} className="w-10 h-10 border border-white/20 flex items-center justify-center hover:border-[#D97645] text-[#F2E7D0]"><Plus size={14} /></button>
                    </div>
                    <div className="mt-2 text-xs text-[#B8A891] text-center">of {r.inventory} available</div>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex justify-between text-xs font-mono-label text-[#B8A891]">
                <span>{totalRooms} room{totalRooms !== 1 ? "s" : ""} selected · sleeps {capacity}</span>
                <span>Total capacity available: 32 guests (4+4+4 rooms)</span>
              </div>
            </div>

            {/* Meal Plans — optional add-on */}
            <div className="md:col-span-2" data-testid="meal-plans-section">
              <div className="flex items-end justify-between mb-3">
                <Label className="font-mono-label text-[#B8A891] flex items-center gap-2"><Utensils size={14} className="text-[#D97645]" /> Add-on meal plans · optional</Label>
                <span className="font-mono-label text-[11px] text-[#B8A891]/70">Per couple per night · {couples} couple{couples !== 1 ? "s" : ""}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* NONE */}
                <button
                  type="button"
                  data-testid="meal-plan-none"
                  onClick={() => setMealPlan("none")}
                  className={`text-left border p-5 transition ${mealPlan === "none" ? "border-[#D97645] bg-[#27221C]" : "border-white/10 hover:border-white/30"}`}
                >
                  <div className="font-mono-label text-[11px] text-[#B8A891]">No meal plan</div>
                  <div className="mt-2 font-display text-2xl text-[#F2E7D0]">Room only</div>
                  <div className="mt-2 text-xs text-[#B8A891] leading-relaxed">Order à la carte from Sunshine Kitchen when you're on the estate.</div>
                  <div className="mt-4 font-mono-label text-[11px] text-[#D97645]">₹ 0 add-on</div>
                </button>

                {MEAL_PLANS.map((m) => {
                  const active = mealPlan === m.id;
                  return (
                    <button
                      key={m.id}
                      type="button"
                      data-testid={`meal-plan-${m.id.toLowerCase()}`}
                      onClick={() => setMealPlan(m.id)}
                      className={`text-left border p-5 transition ${active ? "border-[#D97645] bg-[#27221C]" : "border-white/10 hover:border-white/30"}`}
                    >
                      <div className="font-mono-label text-[11px] text-[#B8A891]">{m.short} · {m.name}</div>
                      <div className="mt-2 font-display text-2xl text-[#F2E7D0]">₹ {m.rate_per_couple_per_night.toLocaleString("en-IN")}</div>
                      <div className="mt-2 text-xs text-[#B8A891] leading-relaxed">{m.includes}</div>
                      <div className="mt-4 font-mono-label text-[11px] text-[#D97645]">Per couple · per night</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Direct Booking Benefits — only on tikratoli.com */}
            <div
              className="md:col-span-2 border border-[#D97645]/40 bg-gradient-to-br from-[#27221C] to-[#1C1913] p-6 md:p-8"
              data-testid="direct-booking-perks"
            >
              <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
                <div>
                  <div className="font-mono-label text-[11px] tracking-[0.28em] text-[#D97645]">EXCLUSIVE</div>
                  <h3 className="mt-2 font-display text-3xl md:text-4xl leading-[1.05] text-[#F2E7D0]">
                    Benefits of booking directly on <em className="italic text-[#D97645]">tikratoli.com</em>
                  </h3>
                </div>
                <div className="font-mono-label text-[10px] tracking-[0.25em] text-[#B8A891]/80 max-w-xs">
                  SHOW YOUR BOOKING ID AT RECEPTION TO AVAIL
                </div>
              </div>
              <ul className="grid grid-cols-1 md:grid-cols-3 gap-4" role="list">
                <li className="flex items-start gap-4 border border-[#3A3229] bg-[#1C1913]/60 p-5">
                  <Coffee size={22} className="text-[#D97645] shrink-0 mt-1" />
                  <div>
                    <div className="font-display text-3xl text-[#F2E7D0] leading-none">20% Off</div>
                    <div className="mt-2 font-mono-label text-[11px] tracking-[0.22em] text-[#B8A891]">FOOD &amp; BEVERAGES</div>
                  </div>
                </li>
                <li className="flex items-start gap-4 border border-[#3A3229] bg-[#1C1913]/60 p-5">
                  <Car size={22} className="text-[#D97645] shrink-0 mt-1" />
                  <div>
                    <div className="font-display text-3xl text-[#F2E7D0] leading-none">10% Off</div>
                    <div className="mt-2 font-mono-label text-[11px] tracking-[0.22em] text-[#B8A891]">JEEP RIDES</div>
                  </div>
                </li>
                <li className="flex items-start gap-4 border border-[#3A3229] bg-[#1C1913]/60 p-5">
                  <Gamepad2 size={22} className="text-[#D97645] shrink-0 mt-1" />
                  <div>
                    <div className="font-display text-3xl text-[#F2E7D0] leading-none">20% Off</div>
                    <div className="mt-2 font-mono-label text-[11px] tracking-[0.22em] text-[#B8A891]">INDOOR GAMES</div>
                  </div>
                </li>
              </ul>
            </div>

            <div className="md:col-span-2">
              <Label className="font-mono-label text-[#B8A891]">Dates</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" data-testid="booking-dates-btn" className="mt-2 w-full h-12 bg-transparent border border-white/20 px-4 flex items-center justify-between text-left text-[#F2E7D0]">
                    <span className="text-sm">{range.from ? format(range.from, "d MMM") : "Check in"} — {range.to ? format(range.to, "d MMM yyyy") : "Check out"}</span>
                    <CalendarIcon size={16} className="text-[#B8A891]" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="bg-[#27221C] border border-white/20 rounded-none" align="start">
                  <Calendar mode="range" selected={range} onSelect={setRange} numberOfMonths={2} disabled={[{ before: new Date() }, ...blockedDates]} modifiers={{ booked: blockedDates }} modifiersClassNames={{ booked: "line-through opacity-50" }} />
                </PopoverContent>
              </Popover>
              <AvailabilityPeek from={range.from} to={range.to} />
            </div>

            <div className="md:col-span-2">
              <Label className="font-mono-label text-[#B8A891]">Notes</Label>
              <Textarea data-testid="booking-message" rows={4} value={form.message} onChange={change("message")} className="mt-2 bg-transparent border-white/20 rounded-none" />
            </div>

            {nights > 0 && totalRooms > 0 && (
              <div className="md:col-span-2 border border-white/10 p-5 space-y-2" data-testid="price-summary">
                {ROOM_TYPES.filter((r) => roomsSelected[r.id] > 0).map((r) => (
                  <div key={r.id} className="flex justify-between text-[#B8A891] text-sm">
                    <span>{roomsSelected[r.id]} × {r.name} × {nights} night{nights > 1 ? "s" : ""}</span>
                    <span className="text-[#F2E7D0]">₹ {(roomsSelected[r.id] * r.price_inr * nights).toLocaleString("en-IN")}</span>
                  </div>
                ))}
                {mealPlanObj && (
                  <div className="flex justify-between text-[#B8A891] text-sm" data-testid="meal-plan-line">
                    <span>{mealPlanObj.short} meal plan · {couples} couple{couples !== 1 ? "s" : ""} × {nights} night{nights > 1 ? "s" : ""}</span>
                    <span className="text-[#F2E7D0]">₹ {mealPlanCost.toLocaleString("en-IN")}</span>
                  </div>
                )}
                <div className="flex justify-between text-[#B8A891] text-sm border-t border-white/10 pt-2">
                  <span>Subtotal</span><span className="text-[#F2E7D0]">₹ {subtotal.toLocaleString("en-IN")}</span>
                </div>
                <div className="flex justify-between text-[#B8A891] text-sm"><span>GST ({payConfig.gst_percent}%)</span><span className="text-[#F2E7D0]">₹ {gst.toLocaleString("en-IN")}</span></div>
                <div className="flex justify-between border-t border-white/10 pt-2 mt-2">
                  <span className="font-mono-label text-[#B8A891]">Total payable</span>
                  <span className="font-display text-xl text-[#D97645]">₹ {grandTotal.toLocaleString("en-IN")}</span>
                </div>
              </div>
            )}

            <div className="md:col-span-2">
              <Button type="submit" data-testid="booking-submit" disabled={loading || capacityShort || totalRooms === 0} className="rounded-none bg-[#D97645] hover:bg-[#C0602F] text-[#F2E7D0] font-mono-label h-14 px-8">
                {loading ? "Sending…" : payConfig.enabled ? "Continue to payment" : "Request reservation"}
              </Button>
            </div>
          </form>
        </FadeIn>
      </section>
    </main>
  );
}
